// hooks/useSlideController.js - PROMPT-ONLY, PNG/PPTX EXPORT ONLY
import { useState, useRef, useMemo, useEffect } from 'react';
import { savePresentation, updatePresentation } from '../firestoreService';
import {
  getTemplatesList,
  renderSlideToCanvas,
  renderSlideBackgroundsOnly,
  exportSlideToPPTX,
  exportAllSlidesToPPTX
} from '../canvas/SlideCanvasRenderer';
import { compressImage } from '../utils/imageCompression';

export function useSlideController({
  user,
  processedSlides,
  slideTemplates,
  slideImages,
  isProcessing,
  isFetchingImages,
  getSlideImageUrl,
  handleReset,
  changeSlideTemplate,
  generateFromPrompt,
  setProcessedSlides,
  setSlideTemplates,
  setSlideImages,
}) {
  // ============ STATE ============
  const [fontsLoaded, setFontsLoaded] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [uploadMode, setUploadMode] = useState(null);
  const [userPrompt, setUserPrompt] = useState('');
  const [showPNGModal, setShowPNGModal] = useState(false);
  const [isExportingPPTX, setIsExportingPPTX] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savedPresentationId, setSavedPresentationId] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [isLoadingPresentation, setIsLoadingPresentation] = useState(false);
  const [currentTemplateIndex, setCurrentTemplateIndex] = useState({});
  const [presentationTitle, setPresentationTitle] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [slideLayoutFilters, setSlideLayoutFilters] = useState({});

  // ============ REFS ============
  const hasSavedRef = useRef(false);
  const canvasRefs = useRef({});
  const layoutFiltersInitialized = useRef(false);

  // ============ COMPUTED VALUES ============
  const templatesList = getTemplatesList();

  // Filter templates based on search term only
  const filteredTemplatesList = useMemo(() => {
    if (!searchTerm.trim()) return templatesList;

    const search = searchTerm.toLowerCase();
    return templatesList.filter(template =>
      template?.name?.toLowerCase().startsWith(search)
    );
  }, [searchTerm, templatesList]);

  // Get filtered templates for a specific slide based on layout filter
  const getFilteredTemplatesForSlide = (slideIndex) => {
    const layoutFilter = slideLayoutFilters[slideIndex] || 'right';
    let filtered = templatesList;

    // Filter by layout type
    if (layoutFilter === 'left') {
      filtered = filtered.filter(t => t.id.endsWith('_left'));
    } else if (layoutFilter === 'none') {
      filtered = filtered.filter(t => t.id.endsWith('_none'));
    } else {
      // 'right' - templates ending with _right OR no suffix
      filtered = filtered.filter(t =>
        t.id.endsWith('_right') ||
        (!t.id.endsWith('_left') && !t.id.endsWith('_none') && !t.id.endsWith('_right'))
      );
    }

    // Fallback: if no templates match, return all
    if (filtered.length === 0) {
      filtered = templatesList;
    }

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(template =>
        template?.name?.toLowerCase().startsWith(search)
      );
    }

    return filtered;
  };

  // ============ UTILITY FUNCTIONS ============
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getLoadingMessage = () => {
    if (!fontsLoaded) {
      return { message: "Loading fonts...", submessage: "Preparing your workspace" };
    }
    if (isLoadingPresentation) {
      return { message: "Loading presentation...", submessage: "Just a moment" };
    }
    if (isExportingPPTX) {
      return { message: "Exporting to PowerPoint...", submessage: "Creating your presentation file" };
    }
    return null;
  };

  const getCurrentTemplate = (slideIndex) => {
    const templateId = slideTemplates[slideIndex] || 'modern_bullets_right';
    return templatesList.find(t => t.id === templateId) || templatesList[0];
  };

  const getLayoutFilter = (slideIndex) => {
    return slideLayoutFilters[slideIndex] || 'right';
  };

  // ============ EVENT HANDLERS ============
  const handlePromptGenerate = async (prompt) => {
    setUploadMode('prompt');
    setUserPrompt(prompt);
    hasSavedRef.current = false;
    setSavedPresentationId(null);
    setPresentationTitle('');

    try {
      const result = await generateFromPrompt(prompt, user?.uid || null);
      // Only switch to the full generator layout once slides actually came
      // back. A moderation block resolves with null (no throw) and a
      // failure throws — in both cases we stay on PromptPage, which shows
      // the loading/error/moderation state inline below the prompt box.
      if (result) {
        setHasStarted(true);
      }
    } catch (error) {
      // Stay on PromptPage — processingError is already set by generateFromPrompt.
    }
  };

  const handleLoadPresentation = async (presentation) => {
    try {
      setIsLoadingPresentation(true);

      hasSavedRef.current = true;
      setSavedPresentationId(presentation.id);
      setSaveError(null);
      setPresentationTitle(presentation.title || 'Untitled');

      setUploadMode(presentation.uploadMode || 'prompt');
      setUserPrompt(presentation.originalPrompt || '');

      const loadedSlides = presentation.slides.map((slide) => ({
        extractedContent: slide.extractedContent,
        timestamp: null,
      }));

      const loadedImages = presentation.imageUrls.map((url, index) => ({
        success: true,
        image: { url: url },
        slideIndex: index
      }));

      setProcessedSlides(loadedSlides);
      setSlideTemplates(presentation.templateIds || []);
      setSlideImages(loadedImages);

      // Initialize layout filters for loaded slides
      const initialFilters = {};
      for (let i = 0; i < loadedSlides.length; i++) {
        initialFilters[i] = i % 2 === 0 ? 'right' : 'left';
      }
      setSlideLayoutFilters(initialFilters);
      layoutFiltersInitialized.current = true;

      setHasStarted(true);

    } catch (error) {

      alert('Failed to load presentation. Please try again.');
    } finally {
      setIsLoadingPresentation(false);
    }
  };

  const handleCompleteReset = () => {
    handleReset();
    setHasStarted(false);
    setUploadMode(null);
    setUserPrompt('');
    setCurrentTemplateIndex({});
    setSearchTerm('');
    setPresentationTitle('');
    setSlideLayoutFilters({});
    layoutFiltersInitialized.current = false;
    hasSavedRef.current = false;
    setSavedPresentationId(null);
    setSaveError(null);
    setShowResults(false);
  };

  // Apply one template's STYLE (its base name, e.g. "glassmorphic_bullets")
  // to every slide in the deck, while preserving each slide's own image
  // layout (left/right/none). This is what keeps all slides visually in
  // sync when the user changes the template on any single slide — previously
  // changeSlideTemplate() only touched the one slide that was passed in.
  const applyTemplateStyleToAllSlides = (templateId) => {
    const baseName = templateId.replace(/_(left|right|none)$/, '');

    setSlideTemplates(prev => {
      const updated = { ...prev };
      const totalSlides = processedSlides.length;

      for (let i = 0; i < totalSlides; i++) {
        const layout = slideLayoutFilters[i] || (i % 2 === 0 ? 'right' : 'left');
        const suffix = layout === 'left' ? '_left' : layout === 'none' ? '_none' : '_right';
        const candidateId = `${baseName}${suffix}`;
        const exists = templatesList.some(t => t.id === candidateId);
        updated[i] = exists ? candidateId : templateId;
      }

      return updated;
    });
  };

  // Handle layout filter change
  const handleLayoutFilterChange = (slideIndex, newLayout) => {

    setSlideLayoutFilters(prev => ({
      ...prev,
      [slideIndex]: newLayout
    }));

    // Get the current template's base name (without suffix)
    const currentTemplateId = slideTemplates[slideIndex] || 'modern_bullets_right';
    const baseName = currentTemplateId.replace(/_(left|none|right)$/, '');

    // Build the new template ID with the layout suffix
    let newTemplateId;
    if (newLayout === 'left') {
      newTemplateId = `${baseName}_left`;
    } else if (newLayout === 'none') {
      newTemplateId = `${baseName}_none`;
    } else {
      newTemplateId = `${baseName}_right`;
    }

    changeSlideTemplate(slideIndex, newTemplateId);

  };

  const handleAnimationToggle = (slideIndex) => {
    const currentTemplateId = slideTemplates[slideIndex] || 'modern_bullets_right';
    const currentTemplate = templatesList.find(t => t.id === currentTemplateId);
    const isCurrentlyAnimated = currentTemplate?.supportsAnimation ?? false;
    const suffixMatch = currentTemplateId.match(/_(left|right|none)$/);
    const layoutSuffix = suffixMatch ? suffixMatch[1] : 'right';
    let newTemplateId;
    if (isCurrentlyAnimated) {
      const candidate = `modern_bullets_${layoutSuffix}`;
      newTemplateId = templatesList.some(t => t.id === candidate && !t.supportsAnimation)
        ? candidate : 'modern_bullets_right';
    } else {
      const candidate = `modern_anim_${layoutSuffix}`;
      newTemplateId = templatesList.some(t => t.id === candidate && t.supportsAnimation)
        ? candidate : 'modern_anim_right';
    }
    applyTemplateStyleToAllSlides(newTemplateId);
  };

  // ✅ MAXIMUM QUALITY: handleImageUpload with minimal compression
  const handleImageUpload = async (slideIndex, file) => {
    try {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        throw new Error('Please select a valid image file');
      }

      // Validate file size - max 10MB
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('Image size must be less than 10MB');
      }

      // ✅ MAXIMUM QUALITY: Only compress if really needed
      const compressedFile = await compressImage(file, {
        maxSizeMB: 5,              // ✅ Only compress if > 5MB (was 3MB)
        maxWidthOrHeight: 2400,    // ✅ Higher res (was 1920)
        quality: 0.98,             // ✅ Near-lossless (was 0.95)
        preserveTransparency: true
      });

      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', compressedFile);
      formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET);
      formData.append('folder', 'slide_images');

      // ✅ Request high quality from Cloudinary
      formData.append('quality', 'auto:best');

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}/image/upload`,
        {
          method: 'POST',
          body: formData,
        }
      );

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();

      if (!data.secure_url) {
        throw new Error('No secure URL returned from Cloudinary');
      }

      // ✅ BEST QUALITY: Cloudinary transformations optimized for presentations
      const optimizedUrl = `${data.secure_url.replace('/upload/', '/upload/q_auto:best,f_auto,w_2400,c_limit/')}`;

      // Update slide images state with optimized URL
      setSlideImages(prev => {
        const updated = [...prev];
        updated[slideIndex] = {
          success: true,
          image: { url: optimizedUrl },
          slideIndex: slideIndex,
          isUserUploaded: true
        };
        return updated;
      });

      // ✅ If presentation is already saved, update it immediately
      if (savedPresentationId && user) {

        try {
          const updatedImageUrls = slideImages.map((img, idx) => {
            if (idx === slideIndex) {
              return optimizedUrl;
            }
            return img?.image?.url || null;
          });

          await updatePresentation(savedPresentationId, {
            imageUrls: updatedImageUrls
          });

        } catch (updateError) {

        }
      } else {
        hasSavedRef.current = false;
      }

    } catch (error) {

      throw error;
    }
  };

  const handleTemplateSelect = (slideIndex, templateId) => {
    applyTemplateStyleToAllSlides(templateId);
  };

  const handleTemplateNext = (slideIndex) => {
    const filteredForSlide = getFilteredTemplatesForSlide(slideIndex);
    const currentTemplateId = slideTemplates[slideIndex] || 'modern_bullets_right';
    const currentIdx = filteredForSlide.findIndex(t => t.id === currentTemplateId);

    if (currentIdx === -1) {
      if (filteredForSlide.length > 0) {
        applyTemplateStyleToAllSlides(filteredForSlide[0].id);
      }
      return;
    }

    const nextIdx = (currentIdx + 1) % filteredForSlide.length;
    setCurrentTemplateIndex(prev => ({ ...prev, [slideIndex]: nextIdx }));
    applyTemplateStyleToAllSlides(filteredForSlide[nextIdx].id);
  };

  const handleTemplatePrev = (slideIndex) => {
    const filteredForSlide = getFilteredTemplatesForSlide(slideIndex);
    const currentTemplateId = slideTemplates[slideIndex] || 'modern_bullets_right';
    const currentIdx = filteredForSlide.findIndex(t => t.id === currentTemplateId);

    if (currentIdx === -1) {
      if (filteredForSlide.length > 0) {
        applyTemplateStyleToAllSlides(filteredForSlide[filteredForSlide.length - 1].id);
      }
      return;
    }

    const prevIdx = currentIdx === 0 ? filteredForSlide.length - 1 : currentIdx - 1;
    setCurrentTemplateIndex(prev => ({ ...prev, [slideIndex]: prevIdx }));
    applyTemplateStyleToAllSlides(filteredForSlide[prevIdx].id);
  };

  const handleDownloadSlidePPTX = async (slide, index) => {
    try {
      const canvas = document.createElement('canvas');
      const templateId = slideTemplates[index] || 'modern_bullets_right';
      const imageUrl = getSlideImageUrl(index);

      await renderSlideToCanvas(canvas, slide.extractedContent, templateId, imageUrl);

      const renderedSlideData = {
        imageData: canvas.toDataURL('image/png', 1.0)
      };

      await exportSlideToPPTX(
        slide.extractedContent,
        templateId,
        index,
        imageUrl,
        renderedSlideData
      );

    } catch (error) {

      alert('Failed to export slide to PPTX');
    }
  };

  const handleDownloadAllPPTX = async () => {
    if (isExportingPPTX) return;

    try {
      setIsExportingPPTX(true);

      const chartSlideIndices = processedSlides
        .map((slide, index) => slide.extractedContent?.chartData ? index : null)
        .filter(i => i !== null);

      const renderedBackgrounds = await renderSlideBackgroundsOnly(
        processedSlides,
        slideTemplates,
        slideImages
      );

      if (chartSlideIndices.length > 0) {

        for (const chartIndex of chartSlideIndices) {
          const canvas = document.createElement('canvas');
          const templateId = slideTemplates[chartIndex] || 'modern_bullets_right';
          const imageUrl = getSlideImageUrl(chartIndex);

          await renderSlideToCanvas(
            canvas,
            processedSlides[chartIndex].extractedContent,
            templateId,
            imageUrl
          );

          renderedBackgrounds[chartIndex] = {
            slideIndex: chartIndex,
            imageData: canvas.toDataURL('image/png', 1.0),
            template: templateId,
            isChartSlide: true,
            hasChart: true,
            hasImage: false
          };

        }
      }

      await exportAllSlidesToPPTX(
        processedSlides,
        slideTemplates,
        slideImages,
        renderedBackgrounds
      );

    } catch (error) {

      alert(`Failed to export PPTX: ${error.message}`);
    } finally {
      setIsExportingPPTX(false);
    }
  };

  // ============ EFFECTS ============
  useEffect(() => {
    if (processedSlides.length > 0 && !showResults) {
      setTimeout(() => {
        setShowResults(true);
      }, 100);
    } else if (processedSlides.length === 0 && showResults) {
      setShowResults(false);
    }
  }, [processedSlides.length, showResults]);

  // Initialize layout filters
  useEffect(() => {
    if (processedSlides.length > 0 && !layoutFiltersInitialized.current) {
      const initialFilters = {};
      for (let i = 0; i < processedSlides.length; i++) {
        initialFilters[i] = i % 2 === 0 ? 'right' : 'left';
      }
      setSlideLayoutFilters(initialFilters);
      layoutFiltersInitialized.current = true;
    }
  }, [processedSlides.length]);

  useEffect(() => {
    if (processedSlides.length > 0 && userPrompt && !presentationTitle) {
      const generatedTitle = userPrompt.trim().substring(0, 40);
      setPresentationTitle(generatedTitle);
    }
  }, [processedSlides.length, userPrompt, presentationTitle]);

  // ✅ Auto-save effect with update logic
  useEffect(() => {
    const autoSavePresentation = async () => {
      // Only proceed if we have user, slides, and not currently processing
      if (
        !user ||
        processedSlides.length === 0 ||
        isProcessing ||
        isFetchingImages ||
        isLoadingPresentation
      ) {
        return;
      }

      try {
        // ✅ CASE 1: Presentation already exists - UPDATE IT
        if (savedPresentationId && !hasSavedRef.current) {

          setIsSaving(true);
          setSaveError(null);

          // Prepare image URLs
          const imageUrls = slideImages.map(img => img?.image?.url || null);

          await updatePresentation(savedPresentationId, {
            slides: processedSlides.map(slide => ({
              extractedContent: slide.extractedContent,
            })),
            templateIds: slideTemplates || [],
            imageUrls: imageUrls || [],
            slideCount: processedSlides.length,
          });

          hasSavedRef.current = true;

        }
        // ✅ CASE 2: No presentation exists - CREATE NEW ONE
        else if (!savedPresentationId && !hasSavedRef.current) {

          setIsSaving(true);
          setSaveError(null);
          hasSavedRef.current = true;

          const presentationData = {
            userId: user.uid,
            userEmail: user.email,
            uploadMode,
            userPrompt,
            processedSlides,
            slideTemplates,
            slideImages,
          };

          const docId = await savePresentation(presentationData);
          setSavedPresentationId(docId);

        }

      } catch (error) {

        setSaveError(error.message);
        hasSavedRef.current = false;
      } finally {
        setIsSaving(false);
      }
    };

    autoSavePresentation();
  }, [
    user,
    processedSlides,
    isProcessing,
    isFetchingImages,
    isLoadingPresentation,
    uploadMode,
    userPrompt,
    slideTemplates,
    slideImages,
    savedPresentationId,
  ]);

  // ============ RETURN ALL STATE AND HANDLERS ============
  return {
    // State
    fontsLoaded,
    setFontsLoaded,
    hasStarted,
    uploadMode,
    userPrompt,
    showPNGModal,
    setShowPNGModal,
    isExportingPPTX,
    showResults,
    isSaving,
    savedPresentationId,
    saveError,
    isLoadingPresentation,
    currentTemplateIndex,
    presentationTitle,
    searchTerm,
    setSearchTerm,
    slideLayoutFilters,

    // Refs
    canvasRefs,

    // Computed
    templatesList,
    filteredTemplatesList,
    getFilteredTemplatesForSlide,
    getLayoutFilter,

    // Utilities
    formatTime,
    getLoadingMessage,
    getCurrentTemplate,

    // Handlers
    handlePromptGenerate,
    handleLoadPresentation,
    handleCompleteReset,
    handleTemplateSelect,
    handleTemplateNext,
    handleTemplatePrev,
    handleDownloadSlidePPTX,
    handleDownloadAllPPTX,
    handleLayoutFilterChange,
    handleImageUpload,
    handleAnimationToggle,
  };
}